import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import SeoPackagesCompare from '../../components/billing/SeoPackagesCompare';
import { api } from '../../context/AuthContext';
import { getCurrentPackageId } from '../../data/seoPackages';
import { toast } from 'sonner';

export default function SEOPackagesComparePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);

  const currentPackageId = useMemo(() => getCurrentPackageId(subscription), [subscription]);
  const hasActiveSubscription = !!(subscription?.has_subscription && subscription?.stripe_subscription_id);

  const fetchSubscription = async () => {
    try {
      const response = await api.get('/billing/subscription');
      setSubscription(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load subscription');
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  const handleSubscribe = async (packageId) => {
    setLoading(true);
    try {
      if (hasActiveSubscription && currentPackageId && currentPackageId !== packageId) {
        const response = await api.post('/billing/switch-plan', { new_package: packageId });
        toast.success(response.data.message || 'Plan switched successfully');
        await fetchSubscription();
      } else {
        const response = await api.post('/billing/create-checkout', { package: packageId });
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to update package');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-seo-packages-compare-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">SEO Packages</h1>
          <p className="text-slate-600 dark:text-slate-400">
            Full plan comparison with all deliverables and package limits.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/dashboard/billing')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Billing
        </Button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Updating plan...
        </div>
      )}

      <SeoPackagesCompare
        currentPackageId={currentPackageId}
        onSubscribe={loading ? null : handleSubscribe}
        title="Detailed SEO Package Comparison"
        subtitle="Compare every included task across all package tiers."
      />
    </div>
  );
}
